import axios from 'axios';

// PUBLIC_URL을 고려한 API 베이스 URL 계산
const getApiBaseURL = () => {
  // 런타임에서 현재 경로를 기준으로 API URL 계산
  const currentPath = window.location.pathname;
  const publicUrl = process.env.PUBLIC_URL || '';
  
  // 현재 경로가 PUBLIC_URL로 시작하는 경우 (서브패스 배포)
  if (publicUrl && currentPath.startsWith(publicUrl)) {
    return `${publicUrl}/api`;
  }
  
  // 환경변수가 설정된 경우 사용, 아니면 기본값
  return process.env.REACT_APP_API_URL || '/api';
};

// axios 기본 설정
const api = axios.create({
  baseURL: getApiBaseURL(),
  headers: {
    'Content-Type': 'application/json',
  },
});

// 요청 인터셉터: JWT 토큰 자동 포함
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 응답 인터셉터: 401 에러 시 로그아웃 처리
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // 토큰이 만료되었거나 유효하지 않은 경우
      localStorage.removeItem('auth_token');
      // 로그인 페이지로 리다이렉트는 AuthContext에서 처리 (PUBLIC_URL 고려)
      const basePath = process.env.PUBLIC_URL || '';
      window.location.href = `${basePath}/login`;
    }
    return Promise.reject(error);
  }
);

// API 관련 서비스 함수들
const apiService = {
  // 인증 관련
  login: (data) => api.post('/login', data),
  logout: () => api.post('/logout'),
  getCurrentUser: () => api.get('/me'),
  changePassword: (data) => api.post('/change-password', data),

  // 호스트 정보
  getHostInfo: () => api.get('/host/info'),
  updateHostInfo: (data) => api.post('/host/update', data),
  getHostResources: () => api.get('/host/resources'),

  // GPU 관리
  getGPUs: (refresh = false) => api.get(`/host/gpus?refresh=${refresh}`),
  getAvailableGPUs: () => api.get('/host/gpus/available'),
  assignGPU: (pciId, vmName) => api.post(`/host/gpus/${pciId}/assign/${vmName}`),
  unassignGPU: (pciId) => api.post(`/host/gpus/${pciId}/unassign`),
  
  // GPU 태그 관리
  getGPUsWithTags: (refresh = false) => api.get(`/host/gpus-with-tags?refresh=${refresh}`),
  getAvailableGPUsWithTags: () => api.get('/host/gpus/available-with-tags'),
  updateGPUTag: (pciId, tag) => api.put(`/host/gpus/${pciId}/tag`, { tag }),

  // GPU 일괄할당 관리
  getBulkAssignableGPUs: () => api.get('/host/gpus/bulk-assignable'),
  updateGPUBulkAssignable: (pciId, bulk_assignable) => api.put(`/host/gpus/${pciId}/bulk-assignable`, { bulk_assignable }),

  // 가상머신 관리
  getVMs: (all = false) => api.get(`/vms?all=${all}`),
  getVMInfo: (name) => api.get(`/vms/${name}/info`),
  getVMStatus: (name) => api.get(`/vms/${name}/status`),
  getVMAccess: (name) => api.get(`/vms/${name}/access`),
  startVM: (name) => api.get(`/vms/${name}/start`),
  shutdownVM: (name) => api.get(`/vms/${name}/shutdown`),
  forceStopVM: (name) => api.post(`/vms/${name}/force-stop`),
  resetVM: (name) => api.get(`/vms/${name}/reset`),
  createVM: (data) => api.post('/vms/create', data),
  bulkCreateVMs: (data) => api.post('/vms/bulk-create', data),
  cloneVM: (sourceVm, data) => api.post(`/vms/${sourceVm}/clone`, data),
  deleteVM: (name, removeStorage = false) => 
    api.delete(`/vms/${name}?remove_storage=${removeStorage}`),
  editVMXML: (name, xml) => api.put(`/vms/${name}/xml`, { xml }),
  getVMConsole: (name, lines = 100) => api.get(`/vms/${name}/console?lines=${lines}`),
  getVMCloudInitIso: (name) => api.get(`/vms/${name}/cloud-init-iso`),
  updateVMCloudInitIso: (name, isoName) => api.put(`/vms/${name}/cloud-init-iso?iso_name=${isoName || ''}`),

  // OS 이미지 관리
  getOSImages: () => api.get('/os-images'),
  uploadOSImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/os-images/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  deleteOSImage: (imageName) => api.delete(`/os-images/${imageName}`),

  // 디스크 볼륨 관리
  getVolumes: () => api.get('/volumes'),
  createVolume: (data) => api.post('/volumes/create', data),
  deleteVolume: (volumeName) => api.delete(`/volumes/${volumeName}`),
  
  // 디스크 마운트 관리
  mountDiskToVM: (vmName, data) => api.post(`/vms/${vmName}/mount-disk`, data),
  unmountDiskFromVM: (vmName, targetDev) => api.post(`/vms/${vmName}/unmount-disk/${targetDev}`),

  // Cloud-init 관리
  createCloudInit: (data) => api.post('/cloud-init/create', data),
  getCloudInitIsos: () => api.get('/cloud-init/list'),
  deleteCloudInitIso: (isoName, force = false) => api.delete(`/cloud-init/${isoName}?force=${force}`),
  checkCloudInitIsoInUse: (isoName) => api.get(`/cloud-init/${isoName}/is-in-use`),

  // 네트워크 관리
  getNetworks: () => api.get('/networks'),
  getAvailableBridges: () => api.get('/networks/available-bridges'),
  createNetwork: (data) => api.post('/networks', data),
  getNetwork: (networkName) => api.get(`/networks/${networkName}`),
  updateNetwork: (networkName, data) => api.put(`/networks/${networkName}`, data),
  deleteNetwork: (networkName) => api.delete(`/networks/${networkName}`),
  getNetworkVMs: (networkName) => api.get(`/networks/${networkName}/vms`),
  updateNetworkDhcp: (networkName) => api.post(`/networks/${networkName}/update-dhcp`),
  
  // VM 네트워크 관리
  assignVMToNetwork: (vmName, data) => api.post(`/vms/${vmName}/networks`, data),
  unassignVMFromNetwork: (vmName, networkName) => api.delete(`/vms/${vmName}/networks/${networkName}`),
  getVMNetworks: (vmName) => api.get(`/vms/${vmName}/networks`),

  // 터미널 관리 (SSH 키 인증 전용)
  connectVMTerminal: (vmName) => {
    return api.post(`/vms/${vmName}/terminal/connect`, {});
  },
  disconnectTerminal: (tunnelId) => api.delete(`/terminals/${tunnelId}`),
  getTerminalStatus: (tunnelId) => api.get(`/terminals/${tunnelId}/status`),
  listTerminals: () => api.get('/terminals'),
  
  // Cloud-init 사용자 정보 조회
  getVMCloudInitUserData: (vmName) => api.get(`/vms/${vmName}/cloud-init-user-data`),
  
  // Cloud-init ISO 정보 조회 (편집용)
  getCloudInitIsoInfo: (isoName) => api.get(`/cloud-init/${isoName}/info`),

  // SSH 키 관리
  getHostSSHKey: () => api.get('/ssh-keys/host'),
  generateHostSSHKey: () => api.post('/ssh-keys/host/generate'),
  regenerateHostSSHKey: () => api.post('/ssh-keys/host/regenerate'),
  updateVMsSSHKeys: () => api.post('/ssh-keys/host/update-vms'),
  getVMsSSHKeys: () => api.get('/ssh-keys/vms'),
  generateVMSSHKey: (vmName) => api.post(`/ssh-keys/vms/${vmName}/generate`),
  deleteVMSSHKey: (vmName) => api.delete(`/ssh-keys/vms/${vmName}`),
  getVMSSHKey: (vmName) => api.get(`/ssh-keys/vms/${vmName}`),

  // VM 서비스 레지스트리 관리
  getVMServices: () => api.get('/vm-services'),
  getVMServiceTypes: () => api.get('/vm-services/types'),
  createVMService: (data) => api.post('/vm-services', data),
  updateVMService: (serviceId, data) => api.put(`/vm-services/${serviceId}`, data),
  deleteVMService: (serviceId) => api.delete(`/vm-services/${serviceId}`),
  toggleVMService: (serviceId, enabled) => api.patch(`/vm-services/${serviceId}/toggle`, { enabled }),
  refreshNginxConfig: () => api.post('/vm-services/refresh-nginx'),
  
  // VM 서비스 임시 접근 토큰 관리
  createVMServiceAccessToken: (serviceId, expiresMinutes = 30) => api.post(`/vm-services/${serviceId}/create-access-token?expires_minutes=${expiresMinutes}`),
  getMyVMServiceTokens: () => api.get('/vm-services/my-tokens'),
  revokeVMServiceToken: (token) => api.delete(`/vm-services/tokens/${token}`),
  revokeAllMyVMServiceTokens: () => api.delete('/vm-services/my-tokens/all'),
  
  // VM 내부 브라우저에서 서비스 열기
  openServiceInVM: (serviceId, sshPassword = null) => {
    const data = sshPassword ? { ssh_password: sshPassword } : {};
    return api.post(`/vm-services/${serviceId}/open-in-vm`, data);
  },

  // VM 프리셋 관리 (구 하드웨어 프리셋)
  getHardwarePresets: () => api.get('/hardware-presets'),
  getHardwarePreset: (presetId) => api.get(`/hardware-presets/${presetId}`),
  createHardwarePreset: (data) => api.post('/hardware-presets', data),
  updateHardwarePreset: (presetId, data) => api.put(`/hardware-presets/${presetId}`, data),
  deleteHardwarePreset: (presetId) => api.delete(`/hardware-presets/${presetId}`),
  applyPresetPortForwards: (presetId, vmName) => api.post(`/hardware-presets/${presetId}/apply-port-forwards/${vmName}`),

  // User Data 템플릿 관리
  getUserDataTemplates: () => api.get('/userdata-templates'),
  getUserDataTemplate: (name) => api.get(`/userdata-templates/${name}`),
  createUserDataTemplate: (data) => api.post('/userdata-templates', data),
  updateUserDataTemplate: (name, data) => api.put(`/userdata-templates/${name}`, data),
  deleteUserDataTemplate: (name) => api.delete(`/userdata-templates/${name}`),

  // 포트 포워딩 관리
  getVMPortForwards: (vmName) => api.get(`/vms/${vmName}/port-forwards`),
  createPortForward: (vmName, data) => api.post(`/vms/${vmName}/port-forwards`, data),
  deletePortForward: (vmName, protocol, hostPort) => api.delete(`/vms/${vmName}/port-forwards/${protocol}/${hostPort}`),
  getPortForwardingConfig: () => api.get('/port-forwarding/config'),
  updatePortForwardingConfig: (data) => api.put('/port-forwarding/config', data),
  getAllPortForwardingRules: () => api.get('/port-forwarding/all-rules'),
  diagnosePortForwarding: () => api.get('/port-forwarding/diagnose'),
  getUsedPorts: () => api.get('/port-forwarding/used-ports'),
  autoAssignPort: (data) => api.post('/port-forwarding/auto-assign', data),
  importPortForwards: (data) => api.post('/port-forwarding/import', data),
  restorePortForwards: () => api.post('/port-forwarding/restore'),
  syncAllPortForwardingIP: () => api.post('/port-forwarding/sync-ip'),
  syncVMPortForwardingIP: (vmName) => api.post(`/port-forwarding/sync-ip/${vmName}`),

  // 스토리지 경로 관리
  getStoragePaths: () => api.get('/storage/paths'),
  updateStoragePaths: (data) => api.put('/storage/paths', data),
  validateStoragePath: (path) => api.post('/storage/paths/validate', null, { params: { path } }),
  getStorageUsage: () => api.get('/storage/usage'),

  // 모니터링 서비스 관리
  getMonitoringStatus: () => api.get('/monitoring/status'),
  startMonitoringService: (serviceId) => api.post(`/monitoring/services/${serviceId}/start`),
  stopMonitoringService: (serviceId) => api.post(`/monitoring/services/${serviceId}/stop`),

  // Golden Image 관리
  getGoldenImages: () => api.get('/golden-images'),
  createGoldenImageFromOS: (imageName, data) => api.post(`/golden-images/from-os-image/${imageName}`, data),
  createGoldenImageFromVM: (vmName, data) => api.post(`/golden-images/from-vm/${vmName}`, data),
  updateGoldenImage: (imageId, data) => api.put(`/golden-images/${imageId}`, data),
  deleteGoldenImage: (imageId) => api.delete(`/golden-images/${imageId}`),
};

// axios 인스턴스와 서비스 객체 모두 export
export { api };
export default apiService; 